import React from 'react';
import type { RuleObject, ValidateErrorEntity } from 'rc-field-form/lib/interface';
import GetFTPServerAdminModelsResponse from '../../Interfaces/Responses/Administration/GetFTPServerAdminModelResponse';
import { ManagedFTPServer } from './ManagedFTPServer';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import GetTenantAdminModelsExResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelExResponse';
import GetReportAdminModelsResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelResponse';
import GetReportAdminModelsExResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelExResponse';
import { ManagedTenant } from './ManagedTenant';
import GetChartAdminModelsResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelResponse';
import GetChartAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelsWithChildrenResponse';
import { ManagedChart } from './ManagedChart';
import GetDataSetAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelResponse';
import GetDataSetAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelsWithChildrenResponse';
import { ManagedDataSet } from './ManagedDataSet';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import GetReportInputAdminModelResponse from '../../Interfaces/Responses/Administration/GetReportInputAdminModelResponse';
import GetDataSetInputAdminModelResponse from '../../Interfaces/Responses/Administration/GetDataSetInputAdminModelResponse';
import GetReportAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelsWithChildrenResponse';
import { ManagedReport } from './ManagedReport';
import { ManagedUser } from './ManagedUser';
import { ManagedDrilldown } from './ManagedDrilldown';
import { DrilldownModelWithChildrenResponse } from '../../Interfaces/Responses/Administration/GetDataSetAdminModelsWithChildrenResponse';
import {
    Divider,
    Modal as ServaModal
 } from '@servahealth/serva-ui-kit';

export type ManagedAdminOperation = 'Add' | 'Upd' | 'Del' | '';

export type ManagedAdminEditorMode = 'blank' | 'selected';

export type ManagedActionModalMode = 'add' | 'update' | 'delete' | 'info';

export type ManagedAdminActionMode = 'idle' | 'add' | 'update' | 'delete';

export type ManagedAdminChildOperation = 'add' | 'update' | 'delete';

export type ManagedActionModalState = {
    open: boolean;
    mode: ManagedActionModalMode;
    title: string;
    message: React.ReactNode;
    isError: boolean;
};

export type ManagedFormRule = RuleObject & {
    field?: string;
};

export type ManagedFormErrorInfo<TValues = unknown> = ValidateErrorEntity<TValues>;

export type ManagedApiError = {
    response?: {
        data?: {
            message?: string;
        };
    };
};

export const buildManagedActionModalState = (
    mode: ManagedActionModalMode,
    title: string,
    message: React.ReactNode,
    isError: boolean = false
): ManagedActionModalState => {
    return {
        open: true,
        mode,
        title,
        message,
        isError,
    };
};

export const getManagedActionModalTitleColor = (mode: ManagedActionModalMode): string => {
    switch (mode) {
        case 'add':
            return 'cornflowerblue';

        case 'update':
            return 'darkgray';

        case 'delete':
            return 'red';

        default:
            return 'black';
    }
};

export const getManagedActionModalMessageColor = (isError: boolean): string => {
    return isError ? 'orange' : 'black';
};

type ManagedActionModalProps = {
    modalState: ManagedActionModalState;
    onOk: () => void;
    onCancel?: () => void;
};

export const mapFTPServerAdminModel = (eachElement: GetFTPServerAdminModelsResponse): ManagedFTPServer => {
    return {
        id: eachElement.id,
        name: eachElement.name,
        server: eachElement.server,
        port: eachElement.port,
        userName: eachElement.userName,
        isSFTP: eachElement.isSFTP,
        format: eachElement.format,
        delimiter: eachElement.delimiter,
        fixedWidthLayout: eachElement.fixedWidthLayout,
        includeColumnHeaders: eachElement.includeColumnHeaders,
        remoteFolder: eachElement.remoteFolder,
        fileNameQuery: eachElement.fileNameQuery,
        pgp_Key_Name: eachElement.pgp_Key_Name,
    };
};

export const mapTenantAdminModel = (eachElement: GetTenantAdminModelsResponse): ManagedTenant => {
    return {
        id: eachElement.id,
        name: eachElement.name,
        connString: eachElement.connString,
        createdBy: eachElement.created_By,
        modifiedBy: eachElement.modified_By,
        deletedBy: eachElement.deleted_By,
        createdTs: eachElement.created_Ts,
        modifiedTs: eachElement.modified_Ts,
        deletedTs: eachElement.deleted_Ts,
        logoImage: eachElement.logoImage,
        nameGeneric: eachElement.nameGeneric,
        hideFromDemo: eachElement.hideFromDemo,
        sqlFirstDayOfWeek: eachElement.sqlFirstDayOfWeek,
        applicationId: eachElement.applicationID,
        linkedDBName: eachElement.linkedDbName,
        urlBase: eachElement.urlBase,
        encryptKey: eachElement.encryptKey,
        encryptCert: eachElement.encryptCert,
    };
};

const mapDrilldownModelWithChildren = (drilldown: DrilldownModelWithChildrenResponse): ManagedDrilldown => {
    return {
        id: drilldown.id,
        sourceDataSetId: drilldown.sourceDataSetID,
        sourceChartDataSetId: drilldown.sourceChartDataSetID,
        sourceValue: drilldown.sourceValue,
        targetDataSetId: drilldown.targetDataSetID,
        targetChartId: drilldown.targetChartID,
        targetDataSetInputId: drilldown.targetDataSetInputID,
        deleted: drilldown.deleted,
        drilldownInputPassthroughMaps: drilldown.drilldownInputPassthroughMaps.map((passthroughMap) => {
            return {
                id: passthroughMap.id,
                drilldownId: passthroughMap.drilldownID,
                sourceDataSetInputId: passthroughMap.sourceDataSetInputID,
                targetDataSetInputId: passthroughMap.targetDataSetInputID,
                deleted: passthroughMap.deleted,
            };
        }),
    };
};

export const mapChartAdminModel = (eachChartElement: GetChartAdminModelsWithChildrenResponse): ManagedChart => {
    return {
        id: eachChartElement.id,
        refreshPeriod: eachChartElement.refreshPeriod,
        dataSetId: eachChartElement.datasetID,
        title: eachChartElement.title,
        createdBy: eachChartElement.createdBy,
        modifiedBy: eachChartElement.modifiedBy,
        deletedBy: eachChartElement.deletedBy,
        createdTs: eachChartElement.createdTs,
        modifiedTs: eachChartElement.modifiedTs,
        deletedTs: eachChartElement.deletedTs,
        showLegend: eachChartElement.showLegend,
        dataPointLabel: eachChartElement.dataPointLabel,
        titleGeneric: eachChartElement.titleGeneric,
        opacity: eachChartElement.opacity,
        yAxisTitle: eachChartElement.yAxisTitle,
        xAxisTitle: eachChartElement.xAxisTitle,
        chartDataSets: eachChartElement.chartDatasets.map(
            (eachChartDataSetElement) => {
                return {
                    id: eachChartDataSetElement.id,
                    clientKey: `server-${eachChartDataSetElement.id}`,
                    chartId: eachChartDataSetElement.chartId,
                    chartTypeId: eachChartDataSetElement.chartTypeId,
                    axis: eachChartDataSetElement.axis,
                    label: eachChartDataSetElement.label,
                    formatting: eachChartDataSetElement.formatting,
                    dataColumn: eachChartDataSetElement.dataColumn,
                    reversed: eachChartDataSetElement.reversed,
                    createdBy: eachChartDataSetElement.createdBy,
                    modifiedBy: eachChartDataSetElement.modifiedBy,
                    deletedBy: eachChartDataSetElement.deletedBy,
                    createdTs: eachChartDataSetElement.createdTs,
                    modifiedTs: eachChartDataSetElement.modifiedTs,
                    deletedTs: eachChartDataSetElement.deletedTs,
                    alternateDSId: eachChartDataSetElement.alternateDSId,
                    alternateDSXColumn: eachChartDataSetElement.alternateDSXColumn,
                    tooltipSource: eachChartDataSetElement.tooltipSource,
                    intervalType: eachChartDataSetElement.intervalType,
                    groupBySplit: eachChartDataSetElement.groupBySplit,
                    groupByDefaultShow: eachChartDataSetElement.groupByDefaultShow,
                    tooltipCustom: eachChartDataSetElement.tooltipCustom,
                    manualAxisMin: eachChartDataSetElement.manualAxisMin,
                    manualAxisMax: eachChartDataSetElement.manualAxisMax,
                    seriesColor: eachChartDataSetElement.seriesColor,

                    drilldowns: eachChartDataSetElement.drilldowns.map(mapDrilldownModelWithChildren),
                };
            }
        ),
    };
};

export const mapUserAdminModel = (eachElement: GetUserAdminModelsResponse): ManagedUser => {
    return {
        id: eachElement.id,
        userName: eachElement.userName,
        email: eachElement.email,
        password: eachElement.password,
        passwordFormatId: eachElement.passwordFormatID,
        passwordSalt: eachElement.passwordSalt,
        adminComment: eachElement.adminComment,
        active: eachElement.active,
        deleted: eachElement.deleted,
        isSystemAccount: eachElement.isSystemAccount,
        systemName: eachElement.systemName,
        lastIPAddress: eachElement.lastIPAddress,
        lastLogin: eachElement.lastLogin,
        firstName: eachElement.firstName,
        lastName: eachElement.lastName,
        phone: eachElement.phone,
        createdBy: eachElement.createdBy,
        modifiedBy: eachElement.modifiedBy,
        deletedBy: eachElement.deletedBy,
        createdTs: eachElement.createdTs,
        modifiedTs: eachElement.modifiedTs,
        deletedTs: eachElement.deletedTs,
        currentToken: eachElement.currentToken,
        isDemo: eachElement.isDemo,
        defaultReportId: eachElement.defaultReportID,
        changePasswordRequired: eachElement.changePasswordRequired,
        reportsOnly: eachElement.reportsOnly,
        securityAdmin: eachElement.securityAdmin,
        techTeam: eachElement.techTeam,
        userAccesses: [],
    };
};

export type SlimDataSetAdminModel = {
    id: number;
    name: string;
    deleted: boolean;
};

export type SlimChartAdminModel = {
    id: number;
    dataSetId: number;
    title: string;
    deleted: boolean;
};

export type SlimUserAdminModel = {
    id: number;
    firstName: string;
    lastName: string;
    deleted: boolean;
};

export type SlimTenantAdminModel = {
    id: number;
    name: string;
    deleted: boolean;
};

export type SlimTenantAdminExModel = {
    tenantID: number;
    tenantName: string;
    userID: number;
    deleted: boolean;
};

export type SlimReportInputAdminModel = {
    id: number;
    title: string;
    deleted: boolean;
};

export type SlimDataSetInputAdminModel = {
    id: number;
    title: string;
};

export type SlimUserAccessReportModel = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    isDashboard: boolean;
    useDatasetInputs: boolean;
    released: boolean;
    apiOnly: boolean;
    exportExcel: boolean;
    exportPDF: boolean;
    exportPassword: string;
    tenantID: number;
    tenantName: string;
    deleted: boolean;
};

export type SlimUserAccessReportExModel = SlimUserAccessReportModel & {
    userID: number;
};

export type SlimFTPServerAdminModel = {
    id: number;
    name: string;
};

export const mapSlimUserAdminModel = (eachUser: GetUserAdminModelsResponse): SlimUserAdminModel => {
    return {
        id: eachUser.id,
        firstName: eachUser.firstName,
        lastName: eachUser.lastName,
        deleted: eachUser.deleted,
    };
};

export const mapSlimTenantAdminModel = (eachTenant: GetTenantAdminModelsResponse): SlimTenantAdminModel => {
    return {
        id: eachTenant.id,
        name: eachTenant.name,
        deleted: eachTenant.deleted,
    };
};

export const mapSlimDataSetAdminModel = (eachDataSet: GetDataSetAdminModelsResponse): SlimDataSetAdminModel => {
    return {
        id: eachDataSet.id,
        name: eachDataSet.name,
        deleted: eachDataSet.deleted
    };
};

export const mapSlimChartAdminModel = (eachChart: GetChartAdminModelsResponse): SlimChartAdminModel => {
    return {
        id: eachChart.id,
        dataSetId: eachChart.dataSet_ID,
        title: eachChart.title,
        deleted: eachChart.deleted,
    };
};

export const mapSlimReportInputAdminModel = (
    eachReportInput: GetReportInputAdminModelResponse
): SlimReportInputAdminModel => {
    return {
        id: eachReportInput.id,
        title: eachReportInput.title,
        deleted: eachReportInput.deleted,
    };
};

export const mapSlimDataSetInputAdminModel = (
    eachDataSetInput: GetDataSetInputAdminModelResponse
): SlimDataSetInputAdminModel => {
    return {
        id: eachDataSetInput.id,
        title: eachDataSetInput.title,
    };
};

export const mapDataSetAdminModel = (eachDataSetElement: GetDataSetAdminModelsWithChildrenResponse): ManagedDataSet => {
    return {
        id: eachDataSetElement.id,
        name: eachDataSetElement.name,
        query: eachDataSetElement.query,
        groupBy: eachDataSetElement.groupBy,
        orderBy: eachDataSetElement.orderBy,
        grouping: eachDataSetElement.grouping ?? '',
        allowFiltering: eachDataSetElement.allowFiltering,
        createdBy: eachDataSetElement.createdBy,
        modifiedBy: eachDataSetElement.modifiedBy,
        deletedBy: eachDataSetElement.deletedBy,
        createdTs: eachDataSetElement.createdTs,
        modifiedTs: eachDataSetElement.modifiedTs,
        deletedTs: eachDataSetElement.deletedTs,
        groupingGeneric: eachDataSetElement.groupingGeneric,
        nameGeneric: eachDataSetElement.nameGeneric,
        hideFromPortal: eachDataSetElement.hideFromPortal,
        alwaysShowFilters: eachDataSetElement.alwaysShowFilters,
        involvesEncryption: eachDataSetElement.involvesEncryption,
        isolationLevel: eachDataSetElement.isolationLevel,

        dataSetSecurities: eachDataSetElement.datasetSecurities
            .filter((eachDataSetSecurityElement) => !eachDataSetSecurityElement.deletedBy && !eachDataSetSecurityElement.deletedTs)
            .map((eachDataSetSecurityElement) => {
            return {
                id: eachDataSetSecurityElement.id,
                dataSetId: eachDataSetSecurityElement.dataSetID,
                userId: eachDataSetSecurityElement.userID,
                tenantId: eachDataSetSecurityElement.tenantID,
                userRoleId: eachDataSetSecurityElement.userRoleID,
                additionalWhere: eachDataSetSecurityElement.additionalWhere,
                createdBy: eachDataSetSecurityElement.createdBy,
                modifiedBy: eachDataSetSecurityElement.modifiedBy,
                deletedBy: eachDataSetSecurityElement.deletedBy,
                createdTs: eachDataSetSecurityElement.createdTs,
                modifiedTs: eachDataSetSecurityElement.modifiedTs,
                deletedTs: eachDataSetSecurityElement.deletedTs,
            };
        }),

        dataSetInputs: eachDataSetElement.datasetInputs
            .filter((eachDataSetInputElement) => !eachDataSetInputElement.deletedBy && !eachDataSetInputElement.deletedTs)
            .map((eachDataSetInputElement) => {
            return {
                id: eachDataSetInputElement.id,
                dataSetId: eachDataSetInputElement.dataSetId,
                title: eachDataSetInputElement.title,
                inputName: eachDataSetInputElement.inputName,
                sourceQuery: eachDataSetInputElement.sourceQuery,
                multiSelect: eachDataSetInputElement.multiSelect,
                selector: eachDataSetInputElement.selector,
                dataFormat: eachDataSetInputElement.dataFormat,
                required: eachDataSetInputElement.required,
                whereQuery: eachDataSetInputElement.whereQuery,
                createdBy: eachDataSetInputElement.createdBy,
                modifiedBy: eachDataSetInputElement.modifiedBy,
                deletedBy: eachDataSetInputElement.deletedBy,
                createdTs: eachDataSetInputElement.createdTs,
                modifiedTs: eachDataSetInputElement.modifiedTs,
                deletedTs: eachDataSetInputElement.deletedTs,
                globalDesignator: eachDataSetInputElement.globalDesignator,
                sort: eachDataSetInputElement.sort,
                parentId: eachDataSetInputElement.parentId,
                defaultValue: eachDataSetInputElement.defaultValue,
            };
        }),

        dataSetColumnFormats: eachDataSetElement.datasetColumnFormats
            .filter((eachDataSetColumnFormatElement) => !eachDataSetColumnFormatElement.deletedBy && !eachDataSetColumnFormatElement.deletedTs)
            .map((eachDataSetColumnFormatElement) => {
            return {
                id: eachDataSetColumnFormatElement.id,
                dataSetId: eachDataSetColumnFormatElement.dataSetID,
                columnName: eachDataSetColumnFormatElement.columnName,
                format: eachDataSetColumnFormatElement.format,
                aggregate: eachDataSetColumnFormatElement.aggregate,
                allowFiltering: eachDataSetColumnFormatElement.allowFiltering,
                groupColumn: eachDataSetColumnFormatElement.groupColumn,
                includeInGrandTotal: eachDataSetColumnFormatElement.includeInGrandTotal,
                visible: eachDataSetColumnFormatElement.visible,
                createdBy: eachDataSetColumnFormatElement.createdBy,
                modifiedBy: eachDataSetColumnFormatElement.modifiedBy,
                deletedBy: eachDataSetColumnFormatElement.deletedBy,
                createdTs: eachDataSetColumnFormatElement.createdTs,
                modifiedTs: eachDataSetColumnFormatElement.modifiedTs,
                deletedTs: eachDataSetColumnFormatElement.deletedTs,
                alignment: eachDataSetColumnFormatElement.alignment,
                colorBG: eachDataSetColumnFormatElement.colorBG,
                colorFore: eachDataSetColumnFormatElement.colorFore,
                formatExcel: eachDataSetColumnFormatElement.formatExcel,
                columnTitle: eachDataSetColumnFormatElement.columnTitle,
                toolTip: eachDataSetColumnFormatElement.toolTip,
                userId: eachDataSetColumnFormatElement.userID,
                widthExcel: eachDataSetColumnFormatElement.widthExcel,
                allowHTML: eachDataSetColumnFormatElement.allowHTML,
                jsDataType: eachDataSetColumnFormatElement.jsDataType,
            };
        }),

        drilldowns: eachDataSetElement.drilldowns.map(mapDrilldownModelWithChildren),
    };
};

export const mapReportAdminModel = (eachReportElement: GetReportAdminModelsWithChildrenResponse): ManagedReport => {
    return {
        id: eachReportElement.id,
        name: eachReportElement.name,
        groupName: eachReportElement.groupName,
        isDashboard: eachReportElement.isDashboard,
        useDatasetInputs: eachReportElement.useDatasetInputs,
        createdBy: eachReportElement.createdBy,
        modifiedBy: eachReportElement.modifiedBy,
        deletedBy: eachReportElement.deletedBy,
        createdTs: eachReportElement.createdTs,
        modifiedTs: eachReportElement.modifiedTs,
        deletedTs: eachReportElement.deletedTs,
        released: eachReportElement.released,
        nameGeneric: eachReportElement.nameGeneric,
        groupNameGeneric: eachReportElement.groupNameGeneric,
        sort: eachReportElement.sort,
        apiOnly: eachReportElement.apiOnly,
        exportExcel: eachReportElement.exportExcel,
        exportPDF: eachReportElement.exportPDF,
        exportPassword: eachReportElement.exportPassword,

        reportSecurities: eachReportElement.reportSecurities.map((eachReportSecurityElement) => {
            return {
                id: eachReportSecurityElement.id,
                reportId: eachReportSecurityElement.reportID,
                userId: eachReportSecurityElement.userID,
                tenantId: eachReportSecurityElement.tenantID,
                userRoleId: eachReportSecurityElement.userRoleID,
                createdBy: eachReportSecurityElement.createdBy,
                modifiedBy: eachReportSecurityElement.modifiedBy,
                deletedBy: eachReportSecurityElement.deletedBy,
                createdTs: eachReportSecurityElement.createdTs,
                modifiedTs: eachReportSecurityElement.modifiedTs,
                deletedTs: eachReportSecurityElement.deletedTs,
            };
        }),

        reportInputs: eachReportElement.reportInputs.map((eachReportInputElement) => {
            return {
                id: eachReportInputElement.id,
                reportId: eachReportInputElement.reportId,
                title: eachReportInputElement.title,
                sourceQuery: eachReportInputElement.sourceQuery,
                multiSelect: eachReportInputElement.multiSelect,
                selector: eachReportInputElement.selector,
                dataFormat: eachReportInputElement.dataFormat,
                required: eachReportInputElement.required,
                createdBy: eachReportInputElement.createdBy,
                modifiedBy: eachReportInputElement.modifiedBy,
                deletedBy: eachReportInputElement.deletedBy,
                createdTs: eachReportInputElement.createdTs,
                modifiedTs: eachReportInputElement.modifiedTs,
                deletedTs: eachReportInputElement.deletedTs,
                globalDesignator: eachReportInputElement.globalDesignator,
                inPageInput: eachReportInputElement.inPageInput,
                parentId: eachReportInputElement.parentId,
                defaultValue: eachReportInputElement.defaultValue,
            };
        }),

        reportPages: eachReportElement.reportPages.map((eachReportPageElement) => {
            return {
                id: eachReportPageElement.id,
                reportId: eachReportPageElement.reportID,
                pageNum: eachReportPageElement.pageNum,
                pageTitle: eachReportPageElement.pageTitle,
                layout: eachReportPageElement.layout,
                pdfLayout: eachReportPageElement.pdfLayout,
                hide: eachReportPageElement.hide,

                reportItems: eachReportPageElement.reportItems.map((eachReportItemElement) => {
                    return {
                        id: eachReportItemElement.id,
                        pageId: eachReportItemElement.pageID,
                        dataSetId: eachReportItemElement.datasetID,
                        chartId: eachReportItemElement.chartID,
                        tenantId: eachReportItemElement.tenantID,
                        refreshRate: eachReportItemElement.refreshRate,
                        createdBy: eachReportItemElement.createdBy,
                        modifiedBy: eachReportItemElement.modifiedBy,
                        deletedBy: eachReportItemElement.deletedBy,
                        createdTs: eachReportItemElement.createdTs,
                        modifiedTs: eachReportItemElement.modifiedTs,
                        deletedTs: eachReportItemElement.deletedTs,
                        hideTitle: eachReportItemElement.hideTitle,

                        reportItemCustomizations: eachReportItemElement.reportItemCustomizations.map((eachReportItemCustomization) => {
                            return {
                                id: eachReportItemCustomization.id,
                                reportItemID: eachReportItemCustomization.reportItemID,
                                userID: eachReportItemCustomization.userID,
                                type: eachReportItemCustomization.type,
                                columnName: eachReportItemCustomization.columnName,
                                value: eachReportItemCustomization.value,
                                valueStr: eachReportItemCustomization.valueStr,
                            };
                        }),

                        reportItemInputMaps: eachReportItemElement.reportItemInputMaps.map((eachReportItemInputMap) => {
                            return {
                                id: eachReportItemInputMap.id,
                                reportInputID: eachReportItemInputMap.reportInputID,
                                dataSetInputID: eachReportItemInputMap.dataSetInputID,
                                reportItemID: eachReportItemInputMap.reportItemID,
                            };
                        }),
                    };
                }),
            };
        }),
    };
};

export const mapSlimTenantAdminExModel = (eachTenant: GetTenantAdminModelsExResponse): SlimTenantAdminExModel => {
    return {
        tenantID: eachTenant.id,
        tenantName: eachTenant.name,
        userID: eachTenant.userID,
        deleted: eachTenant.deleted,
    };
};

export const mapSlimUserAccessReportModel = (eachReport: GetReportAdminModelsResponse): SlimUserAccessReportModel => {
    return {
        reportID: eachReport.id,
        reportName: eachReport.name,
        reportGroupName: eachReport.groupName,
        isDashboard: eachReport.isDashboard,
        useDatasetInputs: eachReport.useDatasetInputs,
        released: eachReport.released,
        apiOnly: eachReport.apiOnly,
        exportExcel: eachReport.exportExcel,
        exportPDF: eachReport.exportPDF,
        exportPassword: eachReport.exportPassword,
        tenantID: eachReport.tenantID,
        tenantName: eachReport.tenantName,
        deleted: eachReport.deleted,
    };
};

export const mapSlimFTPServerAdminModel = (eachFTPServer: GetFTPServerAdminModelsResponse): SlimFTPServerAdminModel => {
    return {
        id: eachFTPServer.id,
        name: eachFTPServer.name,
    };
};

export const mapSlimUserAccessReportExModel = (eachReport: GetReportAdminModelsExResponse): SlimUserAccessReportExModel => {
    return {
        reportID: eachReport.id,
        reportName: eachReport.name,
        reportGroupName: eachReport.groupName,
        isDashboard: eachReport.isDashboard,
        useDatasetInputs: eachReport.useDatasetInputs,
        released: eachReport.released,
        apiOnly: eachReport.apiOnly,
        exportExcel: eachReport.exportExcel,
        exportPDF: eachReport.exportPDF,
        exportPassword: eachReport.exportPassword,
        tenantID: eachReport.tenantID,
        tenantName: eachReport.tenantName,
        userID: eachReport.userID,
        deleted: eachReport.deleted,
    };
};

export const ManagedActionModal: React.FC<ManagedActionModalProps> = ({ modalState, onOk, onCancel, }) => {
    return (
        <ServaModal
            open={modalState.open}
            title={
                <div
                    style={{
                        color: getManagedActionModalTitleColor(modalState.mode),
                        fontSize: '20px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                    }}
                >
                    {modalState.title}
                </div>
            }
            okText='OK'
            cancelButtonProps={{ style: { display: 'none' } }}
            onOk={onOk}
            onCancel={onCancel ?? onOk}
        >
            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
            <div
                style={{
                    color: getManagedActionModalMessageColor(modalState.isError),
                    fontSize: '14px',
                    fontWeight: modalState.isError ? 'bold' : 'normal',
                    textAlign: 'center',
                    width: '100%',
                }}
            >
                {modalState.message}
            </div>
            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
        </ServaModal>
    );
};
